import React, { useState, useCallback, useEffect } from 'react';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { TableSortDirection } from '@amzn/meridian/table/table';
import Box from '@amzn/meridian/box';
import editTokens from '@amzn/meridian-tokens/base/icon/edit';
import { useAppSelector } from 'src/store/store';
import Icon from '@amzn/meridian/icon';
import { useDispatch } from 'react-redux';
import UncontrolledExpandableRow from 'src/components/shared/uncontrolledExpandableRow/uncontrolledExpandableRow';
import {
  setCurrentPage,
  setIsCreateModalOpen,
  setSiteCapacityFormData,
  setTotalPages,
} from 'src/store/updateSiteCapacitySlice/updateSiteCapacitySlice';
import { TableDataRow } from 'src/store/updateSiteCapacitySlice/types';
import { filterSortAndPaginateTableData } from 'src/utils/tableUtils';
import ShiftsPerJobPopover from '../shiftsPerJobPopover/shiftsPerJobPopover';

const ShiftsTable = () => {
  const dispatch = useDispatch();
  const [sortColumn, setSortColumn] = useState<keyof TableDataRow>('name');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('ascending');
  const onSort = useCallback(
    ({ sortColumn, sortDirection }: { sortColumn: keyof TableDataRow; sortDirection: TableSortDirection }) => {
      setSortDirection(sortDirection);
      setSortColumn(sortColumn);
    },
    [],
  );
  const { tableData, searchField, currentPage, perPage } = useAppSelector((state) => state.updateSiteCapacityPage);
  const [sortedTableData, setSortedTableData] = useState(tableData);

  useEffect(() => {
    // filter, sorting, and pagination
    const { paginatedData, totalPages } = filterSortAndPaginateTableData(
      tableData,
      searchField,
      sortColumn,
      sortDirection,
      currentPage,
      ['id', 'shifts'],
      perPage,
    );
    setSortedTableData(paginatedData);
    dispatch(setTotalPages(totalPages));
  }, [sortColumn, sortDirection, tableData, searchField, currentPage, perPage, dispatch]);

  useEffect(() => {
    // go back to first page when filter/sort
    dispatch(setCurrentPage(1));
  }, [searchField, sortColumn, sortDirection, perPage, dispatch]);

  return (
    <Box overflowY="scroll" overflowX="scroll" type="outline" className="update-site-capacity__table">
      <Table
        data-testid="update-site-capacity__table"
        headerRows={1}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSort={onSort}
        showDividers={true}
        fixHeaderRows={true}
        rowComponents={[UncontrolledExpandableRow, TableRow]}
      >
        <TableRow>
          <TableCell sortColumn="name">Job Name</TableCell>
          <TableCell sortColumn="jobType">Job Type</TableCell>
          <TableCell sortColumn="siteSpecific">Site Specific?</TableCell>
          <TableCell sortColumn="active">Active/Inactive</TableCell>
          <TableCell sortColumn="shiftsPerJob">Shifts per Job</TableCell>
          <TableCell sortColumn="currentOpenAccommodations">Current Open Accommodations</TableCell>
          <TableCell sortColumn="totalAccommodation">Total Accommodation Hours</TableCell>
          <TableCell sortColumn="capacityRemaining">Capacity Remaining</TableCell>
          <TableCell>Edit</TableCell>
        </TableRow>
        {sortedTableData.map((row) => (
          <UncontrolledExpandableRow key={row.jobId}>
            <TableCell>{row.name}</TableCell>
            <TableCell>{row.jobType}</TableCell>
            <TableCell>{row.siteSpecific}</TableCell>
            <TableCell>{row.active}</TableCell>
            <TableCell>
              <ShiftsPerJobPopover shifts={row.shifts} />
            </TableCell>
            <TableCell>{row.currentOpenAccommodations}</TableCell>
            <TableCell>{row.totalAccommodation} hrs</TableCell>
            <TableCell>{row.capacityRemaining} hrs</TableCell>
            <TableCell>
              <div
                className="update-site-capacity__edit-icon"
                onClick={() => {
                  dispatch(setIsCreateModalOpen(true));
                  dispatch(setSiteCapacityFormData(row));
                }}
                aria-label="edit shift capacity"
              >
                <Icon tokens={editTokens}></Icon>
              </div>
            </TableCell>
          </UncontrolledExpandableRow>
        ))}
      </Table>
    </Box>
  );
};

export default ShiftsTable;
